import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

// Search text is local: only an option's value (not its label) is submitted.
// Source entry forms opt into allowCustom to also accept new source names.
export default function SearchableSelect({
  id,
  value = "",
  onChange,
  options = [],
  allowCustom = false,
  allowMultiple = false,
  exclusiveValues = ["", "__none__", "__all__"],
  disabled = false,
  required = false,
  placeholder = "輸入文字搜尋或選擇",
  placeholderValues = [],
  emptyMessage = "沒有符合的選項，請試試其他關鍵字",
  className,
  ...inputProps
}) {
  const generatedId = useId();
  const inputId = id || `searchable-${generatedId}`;
  const listId = `${inputId}-options`;
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const composingRef = useRef(false);
  const holdRef = useRef(null);
  const heldRef = useRef(false);
  const [multipleMode, setMultipleMode] = useState(Array.isArray(value));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState(null);
  const selected = options.find((option) => String(option.value) === String(value));
  const selectedValues = Array.isArray(value) ? value : [value];
  const isMultiple = allowMultiple && (multipleMode || Array.isArray(value));
  const isPlaceholder = !allowCustom && (
    Array.isArray(value) ? value.length === 0 : placeholderValues.includes(String(value))
  );
  const keyword = String(query === null ? "" : query).trim().toLocaleLowerCase();
  const filteredOptions = options.filter((option) =>
    !(String(option.value) === "__none__" && placeholderValues.includes("__none__")) &&
    `${option.label} ${option.description || ""} ${option.value}`
      .toLocaleLowerCase()
      .includes(keyword),
  );
  const displayedValue = allowCustom
    ? String(value || "")
    : query !== null
      ? query
      : isPlaceholder
        ? ""
      : Array.isArray(value)
        ? value.length === 1 ? options.find((option) => String(option.value) === value[0])?.label || "" : `已選 ${value.length} 項`
      : required && !value
        ? ""
        : selected?.label || "";
  const close = () => {
    clearTimeout(holdRef.current);
    setOpen(false);
    setQuery(null);
    setActiveIndex(-1);
  };
  const choose = (option) => {
    if (isMultiple) {
      const nextValue = String(option.value);
      if (exclusiveValues.includes(nextValue)) {
        onChange(nextValue);
        setMultipleMode(false);
        close();
        return;
      }
      const current = selectedValues.filter((item) => item && !exclusiveValues.includes(item));
      const next = current.includes(nextValue) ? current.filter((item) => item !== nextValue) : [...current, nextValue];
      onChange(next);
      return;
    }
    onChange(String(option.value));
    close();
  };
  const cancelHold = () => {
    clearTimeout(holdRef.current);
    holdRef.current = null;
  };
  useEffect(() => () => clearTimeout(holdRef.current), []);
  useEffect(() => {
    if (!Array.isArray(value)) setMultipleMode(false);
  }, [value]);

  useEffect(() => {
    if (!allowCustom && !(allowMultiple && Array.isArray(value))) {
      setQuery(null);
      setActiveIndex(-1);
    }
  }, [value, allowCustom, allowMultiple]);

  useEffect(() => {
    if (disabled) {
      clearTimeout(holdRef.current);
      setOpen(false);
      setQuery(null);
    }
  }, [disabled]);

  useEffect(() => {
    inputRef.current?.setCustomValidity(required && !value && !allowCustom ? "請從選單選擇有效選項" : "");
  }, [required, value, allowCustom]);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (event) => {
      if (!rootRef.current?.contains(event.target) && !listRef.current?.contains(event.target)) {
        clearTimeout(holdRef.current);
        setOpen(false);
        setQuery(null);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    const observer = new MutationObserver(() => {
      if (!rootRef.current?.isConnected) {
        setOpen(false);
        setQuery(null);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
      observer.disconnect();
    };
  }, [open]);

  // A viewport-bound portal avoids clipping in scrolling tables and edit modals.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const reposition = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportTop = window.visualViewport?.offsetTop || 0;
      const viewportHeight = (window.visualViewport?.height || window.innerHeight) + viewportTop;
      const width = Math.min(Math.max(rect.width, 240), viewportWidth - 16);
      const below = viewportHeight - rect.bottom - 8;
      const above = rect.top - viewportTop - 8;
      const useBelow = below >= 160 || below >= above;
      const theme = getComputedStyle(rootRef.current);
      const nextPosition = {
        width,
        left: Math.max(8, Math.min(rect.left, viewportWidth - width - 8)),
        ...(useBelow
          ? { top: rect.bottom + 4 }
          : { bottom: window.innerHeight - rect.top + 4 }),
        maxHeight: Math.max(60, Math.min(260, (useBelow ? below : above) - 4)),
        fontFamily: theme.fontFamily,
        fontSize: theme.fontSize,
        fontWeight: theme.fontWeight,
        lineHeight: theme.lineHeight,
        ...Object.fromEntries(["--app-input-bg", "--app-input-text", "--app-border", "--app-surface-soft", "--app-muted-text"]
          .map((name) => [name, theme.getPropertyValue(name)])),
      };
      setPosition((current) => JSON.stringify(current) === JSON.stringify(nextPosition) ? current : nextPosition);
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    window.visualViewport?.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      window.visualViewport?.removeEventListener("resize", reposition);
    };
  }, [open]);

  useEffect(() => {
    if (open && activeIndex >= 0) {
      listRef.current?.querySelectorAll('[role="option"]')[activeIndex]?.scrollIntoView?.({ block: "nearest" });
    }
  }, [activeIndex, open]);

  return (
    <Control ref={rootRef} className={className} $disabled={disabled}>
      <input
        {...inputProps}
        id={inputId}
        ref={inputRef}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && filteredOptions[activeIndex] ? `${listId}-${activeIndex}` : undefined}
        autoComplete="off"
        title={inputProps.title || (allowMultiple ? "可輸入文字搜尋；長按選項 1 秒或 Shift＋Enter 可複選" : "可輸入文字搜尋，或展開選單選擇")}
        disabled={disabled}
        required={required}
        value={displayedValue}
        placeholder={isPlaceholder ? placeholder : selected?.value === "" ? selected.label : placeholder}
        onFocus={(event) => {
          setQuery(null);
          setActiveIndex(-1);
          setOpen(true);
          event.currentTarget.select();
        }}
        onClick={() => setOpen(true)}
        onBlur={(event) => {
          if (!listRef.current?.contains(event.relatedTarget) && !rootRef.current?.contains(event.relatedTarget)) close();
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveIndex(-1);
          setOpen(true);
          if (allowCustom) onChange(event.target.value);
        }}
        onCompositionStart={() => { composingRef.current = true; }}
        onCompositionEnd={() => { composingRef.current = false; }}
        onKeyDown={(event) => {
          if (composingRef.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
          if (allowMultiple && event.key === "Enter" && event.shiftKey) {
            event.preventDefault();
            setOpen(true);
            setMultipleMode(true);
            if (!Array.isArray(value)) onChange(selectedValues.filter((item) => item && !exclusiveValues.includes(item)));
            return;
          }
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((current) =>
              !filteredOptions.length ? -1 : event.key === "ArrowDown"
                ? Math.min(current + 1, filteredOptions.length - 1)
                : Math.max(current - 1, 0),
            );
          } else if (event.key === "Enter" && open) {
            event.preventDefault();
            const option = filteredOptions[activeIndex] ||
              (keyword ? filteredOptions.find((item) => item.label.toLocaleLowerCase() === keyword) ||
                (filteredOptions.length === 1 ? filteredOptions[0] : undefined) : undefined);
            if (option) choose(option);
            else if (allowCustom) close();
          } else if (event.key === "Escape" && open) {
            event.preventDefault();
            event.stopPropagation();
            close();
          }
        }}
      />
      <Toggle
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-label={`展開${inputProps["aria-label"] || "選項"}選單`}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => {
          event.preventDefault();
          const nextOpen = !open;
          inputRef.current?.focus();
          setQuery(null);
          setActiveIndex(-1);
          setOpen(nextOpen);
        }}
      >
        <span aria-hidden="true">▾</span>
      </Toggle>
      {open && position && createPortal(
        <Options ref={listRef} id={listId} role="listbox" aria-multiselectable={isMultiple || undefined} aria-label={inputProps["aria-label"]} style={position}>
          {filteredOptions.length ? filteredOptions.map((option, index) => (
            <Option
              key={option.value}
              data-value={option.value}
              id={`${listId}-${index}`}
              type="button"
              tabIndex={-1}
              role="option"
              aria-selected={selectedValues.includes(String(option.value))}
              $active={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onPointerDown={(event) => {
                event.preventDefault();
                heldRef.current = false;
                cancelHold();
                if (!allowMultiple || event.button > 0 || exclusiveValues.includes(String(option.value))) return;
                holdRef.current = setTimeout(() => {
                  heldRef.current = true;
                  setMultipleMode(true);
                  const current = selectedValues.filter((item) => item && !exclusiveValues.includes(item));
                  onChange([...new Set([...current, String(option.value)])]);
                }, 1000);
              }}
              onPointerUp={cancelHold}
              onPointerCancel={() => { cancelHold(); heldRef.current = false; }}
              onPointerLeave={cancelHold}
              onContextMenu={(event) => { if (allowMultiple) event.preventDefault(); }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (heldRef.current) { heldRef.current = false; return; }
                choose(option);
              }}
            >
              {isMultiple && !exclusiveValues.includes(String(option.value)) && <span aria-hidden="true">{selectedValues.includes(String(option.value)) ? "☑" : "☐"} </span>}{option.label}
              {option.description && <small>{option.description}</small>}
            </Option>
          )) : <Empty role="status">{emptyMessage}</Empty>}
        </Options>, document.body,
      )}
    </Control>
  );
}

const Control = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  align-self: start;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  && input {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: 38px;
    margin: 0;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    padding: 9px 36px 9px 10px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
    font: inherit;
    text-overflow: ellipsis;
  }
  && input:focus-visible {
    outline: 2px solid var(--app-accent, #2563eb);
    outline-offset: 1px;
  }
`;

const Toggle = styled.button`
  && {
    position: absolute;
    top: 1px;
    right: 1px;
    bottom: 1px;
    width: 34px;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 0 5px 5px 0;
    background: transparent;
    color: var(--app-input-text, #202124);
    font: inherit;
    cursor: pointer;
  }
`;

const Options = styled.div`
  position: fixed;
  z-index: 10000;
  box-sizing: border-box;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  box-shadow: 0 5px 20px #0002;
  font: inherit;
`;

const Option = styled.button`
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: 38px;
  padding: 9px 10px;
  border: 0;
  border-radius: 4px;
  text-align: left;
  overflow-wrap: anywhere;
  background: ${({ $active }) => ($active ? "var(--app-surface-soft, #e8f0fe)" : "transparent")};
  color: inherit;
  font: inherit;
  cursor: pointer;
  &:hover { background: var(--app-surface-soft, #e8f0fe); }
  small { display: block; margin-top: 3px; font-size: 11px; color: var(--app-muted-text, #6b7280); }
`;

const Empty = styled.div`
  padding: 10px;
  font-size: 13px;
  overflow-wrap: anywhere;
`;
